import { ReactNode } from 'react';

// types
import { EntitySubtype, EntityType, Financier, TransactionType } from '../../../@types';

// mui
import { Badge, BadgeProps, Box, SxProps, Tooltip, TooltipProps } from '@mui/material';

// theme
import { theme } from '../../../theme';

// icons
import {
	AccountBalanceOutlined as IconAccountBalance,
	AddchartOutlined as IconAddchart,
	AssignmentOutlined as IconAssignment,
	AssignmentReturnOutlined as IconAssignmentReturn,
	CalculateOutlined as IconCalculate,
	CategoryOutlined as IconCategory,
	CheckOutlined as IconCheck,
	CloseOutlined as IconClose,
	ChevronRightOutlined as IconChevronRight,
	CloudUploadOutlined as IconCloudUpload,
	CommentOutlined as IconComment,
	CorporateFareOutlined as IconCorporateFare,
	CreateOutlined as IconCreate,
	DescriptionOutlined as IconDescription,
	DoubleArrowOutlined as IconDoubleArrow,
	ExtensionOutlined as IconExtension,
	FlagOutlined as IconFlag,
	FolderOutlined as IconFolder,
	InfoOutlined as IconInfo,
	InsertDriveFileOutlined as IconInsertDriveFile,
	LocalAtmOutlined as IconLocalAtm,
	LocalOfferOutlined as IconLocalOffer,
	OutboundOutlined as IconOutbound,
	PageviewOutlined as IconPageview,
	PaidOutlined as IconPaid,
	PaymentsOutlined as IconPayments,
	PeopleOutlineOutlined as IconPeople,
	PersonOutlined as IconPerson,
	PriorityHighOutlined as IconPriorityHigh,
	PublicOutlined as IconPublic,
	QuestionMarkOutlined as IconQuestionMark,
	ReplayOutlined as IconReplenishment,
	RequestQuoteOutlined as IconRequestQuote,
	SearchOutlined as IconSearch,
	StoreOutlined as IconStore,
	SwapHorizOutlined as IconTransfer,
	ViewListOutlined as IconList,
	VolunteerActivismOutlined as IconVolunteerActivism,
	WarningAmberOutlined as IconWarning,
} from '@mui/icons-material';

// img
import { LogoWorldBankMark } from '../../img/LogoWorldBankMark';

// entity icon types
export const entityIconTypes: Partial<Record<EntityType, IconCircleProps['type']>> = {
	AUDITOR: 'auditor',
	BENEFICIARY: 'individual',
	BORROWER: 'borrower',
	DONOR: 'donor',
	INTERMEDIARY: 'entity',
	PIU: 'entity',
	SUB_PIU: 'subPiu',
	SUPPLIER: 'supplier',
	TAX_AUTHORITY: 'tax',
};

export const entityIconSubtypes: Record<EntitySubtype, IconCircleProps['type']> = {
	INDIVIDUAL: 'individual',
	ORGANIZATION: 'organization',
};

export const transactionIconTypes: Record<TransactionType, IconCircleProps['type']> = {
	ADVANCE: 'advance',
	CASH_BENEFIT: 'cashBenefit',
	DIRECT: 'direct',
	INITIAL: 'payment',
	NON_CASH_BENEFIT: 'nonCashBenefit',
	OPEX: 'opex',
	PAYMENT: 'payment',
	REIMBURSEMENT: 'reimbursement',
	REPLENISHMENT: 'replenishment',
	TRANSFER: 'transfer',
};

export const financierIconTypes: Record<Financier, IconCircleProps['type']> = {
	COFN: 'loan',
	IBRD: 'loan',
	IDA: 'loan',
	GOV: 'government',
	TF: 'loan',
	OTH: 'other',
};

// props
export const iconCircleColors = [
	'blue',
	'error',
	'green',
	'grey',
	'info',
	'magenta',
	'orange',
	'primary',
	'purple',
	'success',
	'teal',
	'warning',
	'yellow',
] as const;
export const iconCircleSizes = ['xs', 'sm', 'md', 'lg'] as const;
export const iconCircleTypes = [
	'account',
	'advance',
	'auditor',
	'benefit',
	'beneficiary',
	'borrower',
	'cashBenefit',
	'category',
	'check',
	'close',
	'comment',
	'component',
	'contract',
	'direct',
	'nonCashBenefit',
	'default',
	'description',
	'document',
	'documentType',
	'donor',
	'edit',
	'entity',
	'expenseType',
	'form',
	'government',
	'help',
	'individual',
	'invoice',
	'loan',
	'local',
	'next',
	'other',
	'opex',
	'organization',
	'payment',
	'priority',
	'project',
	'reimbursement',
	'replenishment',
	'search',
	'subPiu',
	'supplier',
	'tag',
	'tax',
	'transfer',
	'upload',
	'user',
	'warning',
] as const;

export type IconCircleColor = (typeof iconCircleColors)[number];
export type IconCircleSize = (typeof iconCircleSizes)[number];
export type IconCircleType = (typeof iconCircleTypes)[number];

export interface IconCircleVariant {
	color: IconCircleColor;
	icon: ReactNode;
}

export interface IconCircleProps {
	BadgeProps?: BadgeProps;
	BadgeTooltipProps?: Omit<TooltipProps, 'children'>;
	color?: IconCircleColor;
	icon?: ReactNode;
	scheme?: 'dark' | 'light';
	size?: IconCircleSize;
	TooltipProps?: Omit<TooltipProps, 'children'>;
	type?: IconCircleType;
}

export const IconCircle = ({
	BadgeProps,
	BadgeTooltipProps,
	color,
	icon,
	scheme = 'light',
	size = 'md',
	TooltipProps,
	type = 'default',
}: IconCircleProps) => {
	// vars
	const customSvgSize: Record<string, number> = {
		xs: 16,
		sm: 20,
		md: 20,
		lg: 30,
	};

	const sizes = {
		xs: {
			width: 24,
			height: 24,
			'& svg': {
				width: 14,
				height: 'auto',
				maxHeight: 14,
			},
		},
		sm: {
			width: 32,
			height: 32,
			'& svg': {
				width: 18,
				height: 'auto',
				maxHeight: 18,
			},
		},
		md: {
			width: 42,
			height: 42,
			'& svg': {
				width: 24,
				height: 'auto',
				maxHeight: 24,
			},
		},
		lg: {
			width: 64,
			height: 64,
			'& svg': {
				width: 32,
				height: 'auto',
				maxHeight: 32,
			},
		},
	};

	const colors = {
		blue: {
			light: theme.palette.brand.blue[200],
			dark: theme.palette.brand.blue[700],
		},
		error: {
			light: theme.palette.error.light,
			dark: theme.palette.error.dark,
		},
		green: {
			light: theme.palette.brand.green[100],
			dark: theme.palette.brand.green[700],
		},
		grey: {
			light: theme.palette.brand.grey[200],
			dark: theme.palette.brand.grey[700],
		},
		help: {
			light: theme.palette.brand.grey[200],
			dark: theme.palette.brand.grey[700],
		},
		info: {
			light: theme.palette.info.light,
			dark: theme.palette.info.dark,
		},
		magenta: {
			light: theme.palette.brand.magenta[100],
			dark: theme.palette.brand.magenta[700],
		},
		orange: {
			light: theme.palette.brand.orange[100],
			dark: theme.palette.brand.orange[700],
		},
		primary: {
			light: theme.palette.primary.light,
			dark: theme.palette.primary.main,
		},
		purple: {
			light: theme.palette.brand.purple[100],
			dark: theme.palette.brand.purple[700],
		},
		success: {
			light: theme.palette.success.light,
			dark: theme.palette.success.dark,
		},
		teal: {
			light: theme.palette.brand.teal[100],
			dark: theme.palette.brand.teal[700],
		},
		warning: {
			light: theme.palette.warning.light,
			dark: theme.palette.warning.dark,
		},
		yellow: {
			light: theme.palette.brand.yellow[100],
			dark: theme.palette.brand.yellow[700],
		},
	};

	const types: Record<string, IconCircleVariant> = {
		account: {
			color: 'blue',
			icon: <LogoWorldBankMark style={{ width: customSvgSize[size] }} />,
		},
		advance: {
			color: 'blue',
			icon: <IconDoubleArrow />,
		},
		auditor: {
			color: 'grey',
			icon: <IconPageview />,
		},
		benefit: {
			color: 'blue',
			icon: <IconVolunteerActivism />,
		},
		beneficiary: {
			color: 'magenta',
			icon: <IconPerson />,
		},
		borrower: {
			color: 'grey',
			icon: <IconAccountBalance />,
		},
		cashBenefit: {
			color: 'blue',
			icon: <IconPaid />,
		},
		category: {
			color: 'blue',
			icon: <IconCategory />,
		},
		check: {
			color: 'green',
			icon: <IconCheck />,
		},
		close: {
			color: 'grey',
			icon: <IconClose />,
		},
		comment: {
			color: 'blue',
			icon: <IconComment />,
		},
		component: {
			color: 'blue',
			icon: <IconExtension />,
		},
		contract: {
			color: 'yellow',
			icon: <IconAssignment />,
		},
		direct: {
			color: 'warning',
			icon: <IconOutbound />,
		},
		nonCashBenefit: {
			color: 'blue',
			icon: <IconVolunteerActivism />,
		},
		default: {
			color: 'grey',
			icon: <IconInfo />,
		},
		description: {
			color: 'blue',
			icon: <IconDescription />,
		},
		document: {
			color: 'green',
			icon: <IconInsertDriveFile />,
		},
		documentType: {
			color: 'green',
			icon: <IconInsertDriveFile />,
		},
		donor: {
			color: 'grey',
			icon: <IconAddchart />,
		},
		edit: {
			color: 'grey',
			icon: <IconCreate />,
		},
		entity: {
			color: 'magenta',
			icon: <IconAccountBalance />,
		},
		expenseType: {
			color: 'blue',
			icon: <IconRequestQuote />,
		},
		form: {
			color: 'magenta',
			icon: <IconList />,
		},
		government: {
			color: 'grey',
			icon: <IconAccountBalance />,
		},
		help: {
			color: 'grey',
			icon: <IconQuestionMark />,
		},
		individual: {
			color: 'magenta',
			icon: <IconPerson />,
		},
		invoice: {
			color: 'green',
			icon: <IconRequestQuote />,
		},
		loan: {
			color: 'teal',
			icon: <IconPayments />,
		},
		local: {
			color: 'blue',
			icon: <IconFlag />,
		},
		next: {
			color: 'grey',
			icon: <IconChevronRight />,
		},
		opex: {
			color: 'blue',
			icon: <IconLocalAtm />,
		},
		organization: {
			color: 'magenta',
			icon: <IconPeople />,
		},
		other: {
			color: 'grey',
			icon: <IconPublic />,
		},
		payment: {
			color: 'blue',
			icon: <IconPaid />,
		},
		priority: {
			color: 'warning',
			icon: <IconPriorityHigh />,
		},
		project: {
			color: 'purple',
			icon: <IconFolder />,
		},
		reimbursement: {
			color: 'blue',
			icon: <IconAssignmentReturn />,
		},
		replenishment: {
			color: 'green',
			icon: <IconReplenishment />,
		},
		search: {
			color: 'grey',
			icon: <IconSearch />,
		},
		subPiu: {
			color: 'magenta',
			icon: <IconCorporateFare />,
		},
		supplier: {
			color: 'magenta',
			icon: <IconStore />,
		},
		tag: {
			color: 'blue',
			icon: <IconLocalOffer />,
		},
		tax: {
			color: 'grey',
			icon: <IconCalculate />,
		},
		transfer: {
			color: 'blue',
			icon: <IconTransfer />,
		},
		upload: {
			color: 'grey',
			icon: <IconCloudUpload />,
		},
		user: {
			color: 'orange',
			icon: <IconPerson />,
		},
		warning: {
			color: 'warning',
			icon: <IconWarning />,
		},
	};

	let content = (
		<Box
			sx={(theme) => {
				let sx: SxProps = {
					backgroundColor: theme.palette.brand.grey[200],
					borderRadius: '50%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: theme.palette.brand.grey[700],
					...sizes[size],
				};

				let colorDefined: IconCircleColor = 'grey';

				if (type) {
					colorDefined = types[type].color;
				}

				if (color) {
					colorDefined = color;
				}

				if (scheme === 'light') {
					sx = {
						...sx,
						backgroundColor: colors[colorDefined].light,
						color: colors[colorDefined].dark,
					};
				} else {
					sx = {
						...sx,
						backgroundColor: colors[colorDefined].dark,
						color: colors[colorDefined].light,
					};
				}

				return sx;
			}}>
			{icon || types[type].icon}
		</Box>
	);

	if (BadgeProps) {
		const { badgeContent, ...badgeProps } = BadgeProps;

		let contentBadge = badgeContent;

		if (BadgeTooltipProps) {
			contentBadge = (
				<Tooltip arrow={true} placement="top" {...BadgeTooltipProps}>
					<div>{badgeContent}</div>
				</Tooltip>
			);
		}

		content = (
			<Badge badgeContent={contentBadge} {...badgeProps}>
				{content}
			</Badge>
		);
	}

	if (TooltipProps) {
		content = (
			<Tooltip arrow={true} placement="top" {...TooltipProps}>
				{content}
			</Tooltip>
		);
	}

	return content;
};
